/**
 * <custom-puzzle-download> — retrait du PDF d'une carte personnalisée payée.
 *
 * Lit `?id=&token=`, demande les URL signées à `api-custom-puzzle-download`.
 * Un `403 not_paid` juste après le paiement est normal (le webhook arrive dans
 * les secondes qui suivent) : on réessaie un moment avant de le dire.
 */

const RETRY_DELAY_MS = 4000;
const MAX_ATTEMPTS = 15; // ~1 minute

const TEXT = {
  waitingPayment: 'Paiement en cours de confirmation… Ta carte arrive dans quelques secondes.',
  notPaid:
    'Ton paiement n’est pas encore confirmé. Recharge cette page dans quelques minutes ; ' +
    'si rien ne change, écris-nous en indiquant ton numéro de commande.',
  notFound: 'Ce lien ne correspond à aucune carte. Vérifie qu’il est complet.',
  error: 'Impossible de préparer ta carte pour le moment. Réessaie dans un instant.',
  ready: 'Ta carte est prête.',
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class CustomPuzzleDownload extends HTMLElement {
  connectedCallback() {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    const token = params.get('token');
    if (!id || !token) {
      this.#status(TEXT.notFound);
      return;
    }
    this.#load(id, token);
  }

  async #load(id, token) {
    const url =
      `${this.dataset.apiBase}/api-custom-puzzle-download` +
      `?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      let response;
      try {
        response = await fetch(url, { headers: { 'x-api-key': this.dataset.apiKey } });
      } catch {
        this.#status(TEXT.error);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        this.querySelector('[data-download]').href = data.downloadUrl;
        this.querySelector('[data-open]').href = data.pdfUrl;
        this.querySelector('[data-actions]').hidden = false;
        this.querySelector('[data-hint]').hidden = false;
        this.#status(data.title ? `${TEXT.ready} « ${data.title} »` : TEXT.ready);
        return;
      }
      if (response.status === 404) {
        this.#status(TEXT.notFound);
        return;
      }
      if (response.status !== 403) {
        this.#status(TEXT.error);
        return;
      }

      this.#status(TEXT.waitingPayment);
      await wait(RETRY_DELAY_MS);
    }
    this.#status(TEXT.notPaid);
  }

  #status(text) {
    this.querySelector('[data-status]').textContent = text;
  }
}

if (!customElements.get('custom-puzzle-download')) {
  customElements.define('custom-puzzle-download', CustomPuzzleDownload);
}
